import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Settings,
  LayoutGrid,
  Mail,
  FileText,
  LogOut,
  Save,
  Eye,
  EyeOff,
  GripVertical,
  ChevronRight,
  Edit3,
  Download,
  Upload,
  Database,
  AlertTriangle,
  CheckCircle,
  Copy,
  Plus,
  Trash2,
  Clock,
  Activity,
  Camera,
  Calendar,
  Share2,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Youtube,
  Users,
  Inbox,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import ContentManager from './ContentManager';
import BackupScheduleCard from './BackupScheduleCard';
import AdminUsers from './AdminUsers';
import AdminLeads from './AdminLeads';
import { formatCT, formatCTLabel } from '../utils/time';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_KEY = 'https://customer-assets.emergentagent.com/job_2c74cda5-f28e-477a-a538-792d83f7b37f/artifacts/9pieqpxz_ShireAdvisors_Logo-Key.png';

// Live Clock Component
const LiveClock = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/Chicago',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-white/70 text-sm">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4" />
        <span>{formatDate(time)}</span>
      </div>
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4" />
        <span className="font-mono">{formatTime(time)}</span>
      </div>
    </div>
  );
};

const AdminPanel = ({ isOpen, onClose, onLayoutChange, onContentChange }) => {
  const { user, token, logout, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('layout');
  const [saving, setSaving] = useState(false);
  const [showContentManager, setShowContentManager] = useState(false);
  const fileInputRef = useRef(null);

  // Clone panel state
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [newPanelName, setNewPanelName] = useState('');

  // Backup state
  const [backupInfo, setBackupInfo] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Activity log state
  const [loginAudits, setLoginAudits] = useState(null);
  const [loginSnapshots, setLoginSnapshots] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [zoomedSnapshot, setZoomedSnapshot] = useState(null);

  // Layout state
  const [panels, setPanels] = useState([]);
  const [draggedPanel, setDraggedPanel] = useState(null);

  // Footer settings
  const [footerSettings, setFooterSettings] = useState({
    copyright_text: '© {year} Shire Advisors, LLC. All rights reserved.',
    version: '1.0.0',
    privacy_policy_url: '#',
    terms_url: '#',
  });

  // Social Media settings
  const [socialMedia, setSocialMedia] = useState([
    { id: 'linkedin', name: 'LinkedIn', url: '', enabled: true, order: 0 },
    { id: 'twitter', name: 'Twitter/X', url: '', enabled: true, order: 1 },
    { id: 'facebook', name: 'Facebook', url: '', enabled: true, order: 2 },
    { id: 'instagram', name: 'Instagram', url: '', enabled: false, order: 3 },
    { id: 'youtube', name: 'YouTube', url: '', enabled: false, order: 4 },
  ]);

  // SMTP settings
  const [smtpSettings, setSmtpSettings] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'Shire Advisors',
    use_tls: true,
    enabled: false,
    notify_to: '',
  });
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [smtpPasswordRevealed, setSmtpPasswordRevealed] = useState(false);

  const handleToggleSmtpPassword = async () => {
    // If currently hidden and we haven't fetched the real password yet, fetch it
    if (!showSmtpPassword && !smtpPasswordRevealed && smtpSettings.password === '********') {
      try {
        const res = await axios.get(`${API}/settings/smtp/reveal-password`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSmtpSettings((prev) => ({ ...prev, password: res.data?.password || '' }));
        setSmtpPasswordRevealed(true);
      } catch (err) {
        toast.error('Failed to reveal password');
        return;
      }
    }
    setShowSmtpPassword((v) => !v);
  };

  // Clonable panel types (these have list-based content that can be duplicated)
  const clonablePanelTypes = {
    investments: { name: 'Investments/Portfolio', icon: '💼' },
    services: { name: 'Services', icon: '⚙️' },
    testimonials: { name: 'Testimonials', icon: '💬' },
    'case-studies': { name: 'Case Studies', icon: '📊' },
    faq: { name: 'FAQ', icon: '❓' },
    insights: { name: 'Blog/Insights', icon: '📝' },
  };

  const panelNames = {
    hero: 'Hero Section',
    services: 'Services',
    about: 'About Us & Team',
    testimonials: 'Testimonials',
    'case-studies': 'Case Studies',
    investments: 'Investments & Clients',
    faq: 'FAQ',
    insights: 'Blog/Insights',
    contact: 'Contact Form',
  };

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen]);

  useEffect(() => {
    if (activeTab === 'backup') {
      fetchBackupInfo();
    }
    if (activeTab === 'activity') {
      fetchActivityLog();
    }
  }, [activeTab]);

  const fetchActivityLog = async () => {
    setActivityLoading(true);
    try {
      const [auditsRes, snapsRes] = await Promise.all([
        axios.get(`${API}/analytics/login-audits?limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/analytics/login-snapshots?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setLoginAudits(auditsRes.data || []);
      setLoginSnapshots(snapsRes.data || []);
    } catch (err) {
      toast.error('Failed to load activity log');
      setLoginAudits([]);
      setLoginSnapshots([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      // Fetch layout
      const layoutRes = await axios.get(`${API}/settings/layout`);
      setPanels(layoutRes.data.panels);

      // Fetch social media settings
      const socialRes = await axios.get(`${API}/settings/social-media`);
      if (socialRes.data.social_media) {
        setSocialMedia(socialRes.data.social_media);
      }

      // Fetch all settings (admin)
      const settingsRes = await axios.get(`${API}/settings/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (settingsRes.data.footer) {
        setFooterSettings(settingsRes.data.footer);
      }
      if (settingsRes.data.smtp) {
        setSmtpSettings(settingsRes.data.smtp);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const saveSocialMedia = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/social-media`, socialMedia, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Social media settings saved');
      if (onContentChange) onContentChange();
    } catch (error) {
      toast.error('Failed to save social media settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSocialMedia = (id, field, value) => {
    setSocialMedia(prev => prev.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const getSocialIcon = (id) => {
    switch (id) {
      case 'linkedin': return Linkedin;
      case 'twitter': return Twitter;
      case 'facebook': return Facebook;
      case 'instagram': return Instagram;
      case 'youtube': return Youtube;
      default: return Share2;
    }
  };

  const fetchBackupInfo = async () => {
    try {
      const response = await axios.get(`${API}/backup/info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBackupInfo(response.data);
    } catch (error) {
      console.error('Error fetching backup info:', error);
    }
  };

  const handleExportBackup = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/backup/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Create and download file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shire_advisors_backup_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Backup exported successfully');
    } catch (error) {
      toast.error('Failed to export backup');
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON backup file');
      return;
    }

    // Confirm before restore
    if (!window.confirm('⚠️ This will REPLACE all current content with the backup data. Are you sure you want to continue?')) {
      fileInputRef.current.value = '';
      return;
    }

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/backup/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });

      toast.success(`Backup restored! ${response.data.restored_collections.length} collections restored.`);
      
      // Refresh settings and notify parent
      fetchSettings();
      fetchBackupInfo();
      if (onLayoutChange) onLayoutChange(panels);
      if (onContentChange) onContentChange();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import backup');
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
      fileInputRef.current.value = '';
    }
  };

  // Clone panel functions
  const openCloneDialog = (panel) => {
    setCloneSource(panel);
    setNewPanelName('');
    setShowCloneDialog(true);
  };

  const handleClonePanel = async () => {
    if (!newPanelName.trim()) {
      toast.error('Please enter a name for the new panel');
      return;
    }

    try {
      // Create new panel with unique ID
      const newPanelId = `${cloneSource.type || cloneSource.id}-${Date.now()}`;
      
      const newPanel = {
        id: newPanelId,
        name: newPanelName.trim(),
        type: cloneSource.type || cloneSource.id, // Base type for rendering
        order: panels.length,
        visible: true,
        isCustom: true, // Mark as custom/cloned panel
      };

      // Add to panels list
      const newPanels = [...panels, newPanel];
      setPanels(newPanels);

      // Save to backend
      await axios.put(`${API}/settings/layout`, newPanels, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Create empty content collection for the new panel
      await axios.post(`${API}/content/custom-panel`, {
        panel_id: newPanelId,
        panel_name: newPanelName.trim(),
        panel_type: cloneSource.type || cloneSource.id,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(`Panel "${newPanelName}" created successfully`);
      setShowCloneDialog(false);
      if (onLayoutChange) onLayoutChange(newPanels);
      
    } catch (error) {
      toast.error('Failed to create panel');
      console.error('Clone error:', error);
    }
  };

  const handleDeletePanel = async (panel, index) => {
    if (!panel.isCustom) {
      toast.error('Cannot delete built-in panels');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${panel.name || panelNames[panel.id]}"? This will also delete all content in this panel.`)) {
      return;
    }

    try {
      // Remove from panels
      const newPanels = panels.filter((_, i) => i !== index);
      newPanels.forEach((p, i) => p.order = i);
      setPanels(newPanels);

      // Save layout
      await axios.put(`${API}/settings/layout`, newPanels, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Delete content collection
      await axios.delete(`${API}/content/custom-panel/${panel.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Panel deleted');
      if (onLayoutChange) onLayoutChange(newPanels);
      
    } catch (error) {
      toast.error('Failed to delete panel');
      console.error('Delete error:', error);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedPanel(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedPanel === null || draggedPanel === index) return;

    const newPanels = [...panels];
    const draggedItem = newPanels[draggedPanel];
    newPanels.splice(draggedPanel, 1);
    newPanels.splice(index, 0, draggedItem);

    // Update order values
    newPanels.forEach((panel, i) => {
      panel.order = i;
    });

    setPanels(newPanels);
    setDraggedPanel(index);
  };

  const handleDragEnd = () => {
    setDraggedPanel(null);
  };

  const togglePanelVisibility = async (index) => {
    const newPanels = [...panels];
    newPanels[index].visible = !newPanels[index].visible;
    setPanels(newPanels);
    
    // Auto-save when visibility changes
    try {
      await axios.put(`${API}/settings/layout`, newPanels, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (onLayoutChange) onLayoutChange(newPanels);
      toast.success(`${panelNames[newPanels[index].id]} ${newPanels[index].visible ? 'shown' : 'hidden'}`);
    } catch (error) {
      toast.error('Failed to save visibility change');
      // Revert on error
      newPanels[index].visible = !newPanels[index].visible;
      setPanels(newPanels);
    }
  };

  const saveLayout = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/layout`, panels, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Layout saved successfully');
      if (onLayoutChange) onLayoutChange(panels);
    } catch (error) {
      toast.error('Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  const saveFooterSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/footer`, footerSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Footer settings saved');
    } catch (error) {
      toast.error('Failed to save footer settings');
    } finally {
      setSaving(false);
    }
  };

  const saveSMTPSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/smtp`, smtpSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('SMTP settings saved');
    } catch (error) {
      toast.error('Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  // SMTP test email (sends to the saved notify_to address)
  const [smtpTestSending, setSmtpTestSending] = useState(false);
  const [smtpTestLastOk, setSmtpTestLastOk] = useState(null);

  // Communications log
  const [emailLog, setEmailLog] = useState(null);
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  const [emailLogPreview, setEmailLogPreview] = useState(null);
  const [emailLogPreviewMode, setEmailLogPreviewMode] = useState('html'); // 'html' | 'text'
  const [emailLogResending, setEmailLogResending] = useState(false);

  // Track which failed log entries the user has already seen, so the red-dot
  // badge on the SMTP tab only lights up for NEW failures. Persisted to
  // localStorage so it survives reloads.
  const SEEN_FAILED_KEY = 'shire_seen_failed_email_ids_v1';
  const [seenFailedIds, setSeenFailedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(SEEN_FAILED_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch (_) {
      return new Set();
    }
  });

  const unseenFailedCount = (() => {
    if (!emailLog || !emailLog.length) return 0;
    // If an entry has been successfully resent later (auto-retry or manual),
    // consider it resolved — no red-dot needed.
    const resolvedIds = new Set(
      emailLog
        .filter((e) => e && e.success === true && e.resent_from)
        .map((e) => e.resent_from)
    );
    return emailLog.filter(
      (e) => e && e.success === false && e.id
        && !resolvedIds.has(e.id)
        && !seenFailedIds.has(e.id)
    ).length;
  })();

  const resendEmailLogEntry = async () => {
    if (!emailLogPreview?.id) {
      toast.error('This entry is too old to resend (missing id)');
      return;
    }
    setEmailLogResending(true);
    try {
      await axios.post(
        `${API}/settings/email-log/${emailLogPreview.id}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Resent to ${emailLogPreview.to}`);
      setEmailLogPreview(null);
      // Refresh the log so the new entry appears at the top.
      try {
        const res = await axios.get(`${API}/settings/email-log?limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmailLog(res.data || []);
      } catch (_) { /* non-fatal */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resend failed');
    } finally {
      setEmailLogResending(false);
    }
  };

  const fetchEmailLog = async () => {
    setEmailLogLoading(true);
    try {
      const res = await axios.get(`${API}/settings/email-log?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmailLog(res.data || []);
    } catch (e) {
      setEmailLog([]);
    } finally {
      setEmailLogLoading(false);
    }
  };

  const clearEmailLog = async () => {
    if (!window.confirm('Delete every entry in the Communications Log? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/settings/email-log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Communications log cleared');
      fetchEmailLog();
    } catch (e) {
      toast.error('Failed to clear log');
    }
  };

  // Auto-load log when SMTP tab opens
  useEffect(() => {
    if (activeTab === 'smtp' && isSuperAdmin() && emailLog === null) {
      fetchEmailLog();
    }
    // Also: opening the SMTP tab means the admin has seen the current
    // failed entries — clear the red-dot badge.
    if (activeTab === 'smtp' && isSuperAdmin() && emailLog && emailLog.length) {
      const failedIds = emailLog
        .filter((e) => e && e.success === false && e.id)
        .map((e) => e.id);
      if (failedIds.length) {
        setSeenFailedIds((prev) => {
          const next = new Set(prev);
          let added = false;
          failedIds.forEach((id) => {
            if (!next.has(id)) { next.add(id); added = true; }
          });
          if (added) {
            try {
              localStorage.setItem(SEEN_FAILED_KEY, JSON.stringify([...next]));
            } catch (_) { /* localStorage unavailable */ }
          }
          return added ? next : prev;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, emailLog]);

  // Panel-open hydration: fetch the log once when the drawer opens so the
  // red-dot badge on the SMTP tab is accurate regardless of which tab is
  // currently active.
  useEffect(() => {
    if (isOpen && isSuperAdmin() && emailLog === null) {
      fetchEmailLog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const sendSmtpTestEmail = async () => {
    if (!smtpSettings.notify_to) {
      toast.error('Enter a Notification Recipient first');
      return;
    }
    setSmtpTestSending(true);
    try {
      const res = await axios.post(
        `${API}/settings/smtp/test`,
        { to: smtpSettings.notify_to },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Test email sent to ${res.data?.to || smtpSettings.notify_to}`);
      setSmtpTestLastOk(new Date());
      fetchEmailLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
      fetchEmailLog();
    } finally {
      setSmtpTestSending(false);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'content', name: 'Edit Content', icon: Edit3 },
    { id: 'layout', name: 'Panel Layout', icon: LayoutGrid },
    { id: 'social', name: 'Social Media', icon: Share2 },
    { id: 'footer', name: 'Footer Settings', icon: FileText },
    ...(isSuperAdmin() ? [
      { id: 'leads', name: 'Leads', icon: Inbox },
      { id: 'activity', name: 'Activity Log', icon: Activity },
      { id: 'smtp', name: 'SMTP Settings', icon: Mail },
      { id: 'backup', name: 'Backup & Restore', icon: Database },
      { id: 'users', name: 'Users', icon: Users },
    ] : []),
  ];

  return (
    <>
    <div className="fixed inset-0 z-[100] flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative ml-auto w-full max-w-6xl bg-white shadow-2xl h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-navy-900 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center p-1.5 border border-gold-500/30">
                <img src={LOGO_KEY} alt="Shire Advisors" className="w-full h-full object-contain" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Admin Panel</h2>
                <p className="text-white/60 text-sm">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
              <button
                onClick={onClose}
                className="text-white/70 hover:text-white transition-colors p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
          {/* Live Clock */}
          <LiveClock />
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6 overflow-x-auto">
          <div className="flex gap-5 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'content') {
                    setShowContentManager(true);
                  } else {
                    setActiveTab(tab.id);
                  }
                }}
                className={`flex items-center gap-2 py-4 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id && tab.id !== 'content'
                    ? 'border-gold-500 text-navy-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4 flex-shrink-0" />
                {tab.name}
                {tab.id === 'smtp' && unseenFailedCount > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shadow-sm"
                    title={`${unseenFailedCount} failed email${unseenFailedCount === 1 ? '' : 's'} — click to review`}
                    data-testid="smtp-tab-failed-badge"
                  >
                    {unseenFailedCount > 9 ? '9+' : unseenFailedCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Layout Tab */}
          {activeTab === 'layout' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Drag and drop panels to reorder. Clone panels to create new sections. Toggle visibility with the eye icon.
              </p>

              <div className="space-y-2">
                {panels
                  .sort((a, b) => a.order - b.order)
                  .map((panel, index) => (
                    <div
                      key={panel.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-3 p-4 bg-gray-50 rounded-lg border-2 transition-all cursor-move ${
                        draggedPanel === index
                          ? 'border-gold-500 bg-gold-50'
                          : 'border-transparent hover:border-gray-200'
                      } ${!panel.visible ? 'opacity-50' : ''}`}
                    >
                      <GripVertical className="w-5 h-5 text-gray-400" />
                      <div className="flex-1">
                        <span className="font-medium text-navy-900">
                          {panel.name || panelNames[panel.id] || panel.id}
                        </span>
                        {panel.isCustom && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            Custom
                          </span>
                        )}
                      </div>
                      
                      {/* Clone button - only for clonable types */}
                      {clonablePanelTypes[panel.type || panel.id] && (
                        <button
                          onClick={() => openCloneDialog(panel)}
                          className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Clone this panel"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Delete button - only for custom panels */}
                      {panel.isCustom && (
                        <button
                          onClick={() => handleDeletePanel(panel, index)}
                          className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete this panel"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => togglePanelVisibility(index)}
                        className={`p-2 rounded-lg transition-colors ${
                          panel.visible
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                      >
                        {panel.visible ? (
                          <Eye className="w-5 h-5" />
                        ) : (
                          <EyeOff className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  ))}
              </div>

              {/* Add New Panel Button */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-600 mb-3">Add a new panel based on an existing type:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(clonablePanelTypes).map(([type, info]) => (
                    <Button
                      key={type}
                      variant="outline"
                      size="sm"
                      onClick={() => openCloneDialog({ id: type, type })}
                      className="text-sm"
                    >
                      <span className="mr-1">{info.icon}</span>
                      {info.name}
                    </Button>
                  ))}
                </div>
              </div>

              <Button
                onClick={saveLayout}
                disabled={saving}
                className="w-full mt-6 bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold"
              >
                {saving ? 'Saving...' : 'Save Layout'}
                <Save className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Social Media Tab */}
          {activeTab === 'social' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  Configure your social media links. Toggle switches to show/hide buttons in the footer.
                </p>
              </div>

              {socialMedia.sort((a, b) => a.order - b.order).map((social) => {
                const Icon = getSocialIcon(social.id);
                return (
                  <div 
                    key={social.id} 
                    className={`border rounded-lg p-4 transition-all ${
                      social.enabled ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${social.enabled ? 'bg-navy-100' : 'bg-gray-200'}`}>
                          <Icon className={`w-5 h-5 ${social.enabled ? 'text-navy-900' : 'text-gray-500'}`} />
                        </div>
                        <span className="font-medium text-navy-900">{social.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{social.enabled ? 'Visible' : 'Hidden'}</span>
                        <Switch
                          checked={social.enabled}
                          onCheckedChange={(checked) => updateSocialMedia(social.id, 'enabled', checked)}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Profile URL</label>
                      <Input
                        value={social.url}
                        onChange={(e) => updateSocialMedia(social.id, 'url', e.target.value)}
                        placeholder={`https://${social.id}.com/yourprofile`}
                        disabled={!social.enabled}
                      />
                    </div>
                  </div>
                );
              })}

              <Button
                onClick={saveSocialMedia}
                disabled={saving}
                className="w-full bg-gold-500 hover:bg-gold-600 text-navy-900"
              >
                {saving ? 'Saving...' : 'Save Social Media Settings'}
                <Save className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Footer Tab */}
          {activeTab === 'footer' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-navy-900 mb-2">
                  Copyright Text
                </label>
                <Textarea
                  value={footerSettings.copyright_text}
                  onChange={(e) =>
                    setFooterSettings((prev) => ({
                      ...prev,
                      copyright_text: e.target.value,
                    }))
                  }
                  placeholder="Use {year} for dynamic year"
                  rows={2}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use {'{year}'} to automatically insert the current year
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-900 mb-2">
                  Privacy Policy URL
                </label>
                <Input
                  value={footerSettings.privacy_policy_url}
                  onChange={(e) =>
                    setFooterSettings((prev) => ({
                      ...prev,
                      privacy_policy_url: e.target.value,
                    }))
                  }
                  placeholder="/privacy-policy"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-900 mb-2">
                  Terms of Service URL
                </label>
                <Input
                  value={footerSettings.terms_url}
                  onChange={(e) =>
                    setFooterSettings((prev) => ({
                      ...prev,
                      terms_url: e.target.value,
                    }))
                  }
                  placeholder="/terms"
                />
              </div>

              <Button
                onClick={saveFooterSettings}
                disabled={saving}
                className="w-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold"
              >
                {saving ? 'Saving...' : 'Save Footer Settings'}
                <Save className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Activity Log Tab */}
          {activeTab === 'activity' && isSuperAdmin() && (
            <div className="space-y-8" data-testid="activity-log-panel">
              {/* Login Audits */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-navy-900 flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Login Audits
                    </h3>
                    <p className="text-xs text-gray-500">
                      Every login attempt — success or failure — across all
                      access levels
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={async () => {
                        if (!window.confirm('Delete all login attempts, snapshots and pageviews from automated test / cluster IP ranges? Real-visitor entries will be preserved.')) return;
                        try {
                          const res = await axios.delete(`${API}/analytics/clear-test-entries`, {
                            headers: { Authorization: `Bearer ${token}` },
                          });
                          const d = res.data?.deleted || {};
                          toast.success(`Cleared ${d.login_audits || 0} audits, ${d.login_snapshots || 0} snapshots, ${d.pageviews || 0} pageviews`);
                          fetchActivityLog();
                        } catch (e) {
                          toast.error('Failed to clear test entries');
                        }
                      }}
                      className="text-xs text-red-600 hover:text-red-700 font-medium"
                      disabled={activityLoading}
                      data-testid="clear-test-entries-btn"
                    >
                      Clear test entries
                    </button>
                    <button
                      onClick={fetchActivityLog}
                      className="text-xs text-gold-600 hover:text-gold-700 font-medium"
                      disabled={activityLoading}
                    >
                      {activityLoading ? 'Loading…' : 'Refresh'}
                    </button>
                  </div>
                </div>

                {activityLoading && loginAudits === null ? (
                  <p className="text-sm text-gray-500 py-6 text-center">Loading audits…</p>
                ) : (loginAudits || []).length === 0 ? (
                  <p className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded-lg border border-gray-100">
                    No login attempts recorded yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">When</th>
                          <th className="text-left px-3 py-2 font-medium">Email</th>
                          <th className="text-left px-3 py-2 font-medium">IP</th>
                          <th className="text-left px-3 py-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {loginAudits.slice(0, 50).map((a, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-xs text-gray-500 whitespace-nowrap">
                              {a.timestamp ? formatCT(a.timestamp) : '—'}
                            </td>
                            <td className="px-3 py-2 text-navy-900">{a.email}</td>
                            <td className="px-3 py-2 font-mono text-xs text-gray-700">{a.ip}</td>
                            <td className="px-3 py-2">
                              {a.success ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                  <CheckCircle className="w-3 h-3" />
                                  OK
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                  Failed
                                  {a.reason && (
                                    <span className="font-normal lowercase opacity-70">· {a.reason}</span>
                                  )}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Level 2 Snapshots */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-navy-900 flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Level 2 Webcam Snapshots
                    </h3>
                    <p className="text-xs text-gray-500">
                      Photos captured during Level 2 logins. Click any thumbnail to enlarge.
                    </p>
                  </div>
                </div>

                {activityLoading && loginSnapshots === null ? (
                  <p className="text-sm text-gray-500 py-6 text-center">Loading snapshots…</p>
                ) : (loginSnapshots || []).length === 0 ? (
                  <p className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded-lg border border-gray-100">
                    No Level 2 snapshots recorded yet.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {loginSnapshots.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setZoomedSnapshot(s)}
                        className="group relative bg-slate-900 rounded-lg overflow-hidden border border-gray-200 hover:border-cyan-400 transition-all text-left"
                        data-testid={`snapshot-thumb-${i}`}
                      >
                        {s.photo && s.photo.startsWith('data:image') ? (
                          <img
                            src={s.photo}
                            alt={`${s.email} login snapshot`}
                            loading="lazy"
                            className="w-full h-32 object-cover"
                          />
                        ) : (
                          <div className="w-full h-32 flex items-center justify-center text-gray-500 text-xs">
                            No image
                          </div>
                        )}
                        <div className="px-2 py-1.5 bg-white">
                          <div className="text-xs font-semibold text-navy-900 truncate">{s.email}</div>
                          <div className="text-[10px] text-gray-500 font-mono">
                            {s.timestamp ? formatCT(s.timestamp) : ''}
                          </div>
                          <div className="text-[10px] text-gray-400 font-mono truncate">{s.ip}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* SMTP Tab */}
          {activeTab === 'smtp' && isSuperAdmin() && (
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-navy-900">Enable SMTP</p>
                  <p className="text-sm text-gray-500">
                    Turn on to send emails from contact form
                  </p>
                </div>
                <Switch
                  checked={smtpSettings.enabled}
                  onCheckedChange={(checked) =>
                    setSmtpSettings((prev) => ({ ...prev, enabled: checked }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-900 mb-2">
                    SMTP Host
                  </label>
                  <Input
                    value={smtpSettings.host}
                    onChange={(e) =>
                      setSmtpSettings((prev) => ({
                        ...prev,
                        host: e.target.value,
                      }))
                    }
                    placeholder="smtp.example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-900 mb-2">
                    Port
                  </label>
                  <Input
                    type="number"
                    value={smtpSettings.port}
                    onChange={(e) =>
                      setSmtpSettings((prev) => ({
                        ...prev,
                        port: parseInt(e.target.value) || 587,
                      }))
                    }
                    placeholder="587"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-900 mb-2">
                  Username
                </label>
                <Input
                  value={smtpSettings.username}
                  onChange={(e) =>
                    setSmtpSettings((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }))
                  }
                  placeholder="username@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-900 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showSmtpPassword ? 'text' : 'password'}
                    value={smtpSettings.password}
                    onChange={(e) =>
                      setSmtpSettings((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    className="pr-10"
                    placeholder="••••••••"
                    data-testid="smtp-password-input"
                  />
                  <button
                    type="button"
                    onClick={handleToggleSmtpPassword}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy-900 transition-colors"
                    aria-label={showSmtpPassword ? 'Hide password' : 'Show password'}
                    title={showSmtpPassword ? 'Hide password' : 'Show password'}
                    data-testid="smtp-password-toggle"
                    tabIndex={-1}
                  >
                    {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy-900 mb-2">
                    From Email
                  </label>
                  <Input
                    value={smtpSettings.from_email}
                    onChange={(e) =>
                      setSmtpSettings((prev) => ({
                        ...prev,
                        from_email: e.target.value,
                      }))
                    }
                    placeholder="noreply@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy-900 mb-2">
                    From Name
                  </label>
                  <Input
                    value={smtpSettings.from_name}
                    onChange={(e) =>
                      setSmtpSettings((prev) => ({
                        ...prev,
                        from_name: e.target.value,
                      }))
                    }
                    placeholder="Shire Advisors"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-navy-900">Use TLS</p>
                  <p className="text-sm text-gray-500">Recommended for security</p>
                </div>
                <Switch
                  checked={smtpSettings.use_tls}
                  onCheckedChange={(checked) =>
                    setSmtpSettings((prev) => ({ ...prev, use_tls: checked }))
                  }
                />
              </div>

              <Button
                onClick={saveSMTPSettings}
                disabled={saving}
                className="w-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold"
              >
                {saving ? 'Saving...' : 'Save SMTP Settings'}
                <Save className="w-4 h-4 ml-2" />
              </Button>

              {/* Notification recipient — used for contact form submissions AND test email */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-cyan-100 rounded-lg">
                    <Mail className="w-4 h-4 text-cyan-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-navy-900 text-sm">Notification Recipient</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Contact-form submissions are emailed to this address. The test button below also sends here.
                      Save SMTP Settings after editing.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="email"
                    value={smtpSettings.notify_to || ''}
                    onChange={(e) =>
                      setSmtpSettings((prev) => ({ ...prev, notify_to: e.target.value }))
                    }
                    placeholder="recipient@example.com"
                    className="flex-1"
                    data-testid="smtp-notify-to-input"
                  />
                  <Button
                    onClick={sendSmtpTestEmail}
                    disabled={smtpTestSending || !smtpSettings.notify_to}
                    className="bg-cyan-700 hover:bg-cyan-800 text-white whitespace-nowrap"
                    data-testid="smtp-test-send-button"
                  >
                    {smtpTestSending ? 'Sending…' : 'Send Test'}
                  </Button>
                </div>

                {smtpTestLastOk && (
                  <p className="mt-2 text-xs text-emerald-700 inline-flex items-center gap-1.5" data-testid="smtp-test-last-ok">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Test passed at {formatCTLabel(smtpTestLastOk)}
                  </p>
                )}
              </div>

              {/* Communications Log */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-navy-900 text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Communications Log
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Every email this site sends — test emails and contact-form notifications.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={clearEmailLog}
                      className="text-xs text-red-600 hover:text-red-700 font-medium"
                      disabled={emailLogLoading || !(emailLog && emailLog.length)}
                      data-testid="email-log-clear-btn"
                    >
                      Clear log
                    </button>
                    <button
                      onClick={fetchEmailLog}
                      className="text-xs text-gold-600 hover:text-gold-700 font-medium"
                      disabled={emailLogLoading}
                    >
                      {emailLogLoading ? 'Loading…' : 'Refresh'}
                    </button>
                  </div>
                </div>

                {emailLogLoading && emailLog === null ? (
                  <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
                ) : (emailLog || []).length === 0 ? (
                  <p className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded-lg border border-gray-100">
                    No emails sent yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200" data-testid="email-log-table">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">When</th>
                          <th className="text-left px-3 py-2 font-medium">Kind</th>
                          <th className="text-left px-3 py-2 font-medium">To</th>
                          <th className="text-left px-3 py-2 font-medium">Subject</th>
                          <th className="text-left px-3 py-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {emailLog.map((e, i) => (
                          <tr
                            key={i}
                            className="hover:bg-gold-50 cursor-pointer align-top transition-colors"
                            onClick={() => {
                              setEmailLogPreview(e);
                              setEmailLogPreviewMode(e.body_html ? 'html' : 'text');
                            }}
                            data-testid={`email-log-row-${i}`}
                          >
                            <td className="px-3 py-2 font-mono text-xs text-gray-500 whitespace-nowrap">
                              {e.timestamp ? formatCT(e.timestamp) : '—'}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {(() => {
                                const k = e.kind;
                                if (k === 'contact_notification') {
                                  return (
                                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-cyan-100 text-cyan-700">
                                      Contact
                                    </span>
                                  );
                                }
                                if (k === 'backup') {
                                  return (
                                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700">
                                      Backup
                                    </span>
                                  );
                                }
                                return (
                                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gold-100 text-gold-700">
                                    Test
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-3 py-2 text-navy-900 break-all">{e.to}</td>
                            <td className="px-3 py-2 text-gray-700 break-all max-w-xs">{e.subject}</td>
                            <td className="px-3 py-2">
                              {e.success ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                  <CheckCircle className="w-3 h-3" />
                                  Sent
                                </span>
                              ) : (
                                <span className="inline-flex flex-col items-start gap-0.5">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                    Failed
                                  </span>
                                  {e.error && (
                                    <span className="text-[10px] text-gray-500 font-normal max-w-xs break-all">{e.error}</span>
                                  )}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Email Preview Modal */}
                {emailLogPreview && (
                  <div
                    className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setEmailLogPreview(null)}
                    data-testid="email-log-preview-modal"
                  >
                    <div
                      className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-navy-900 to-navy-800 text-white">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs uppercase tracking-wider text-gold-400 font-bold mb-1">
                            {emailLogPreview.kind === 'contact_notification' ? 'Contact form lead'
                              : emailLogPreview.kind === 'contact_confirmation' ? 'Visitor confirmation'
                              : emailLogPreview.kind === 'backup' ? 'Database backup'
                              : 'SMTP test'}
                          </p>
                          <h3 className="font-semibold text-base break-all">{emailLogPreview.subject || '(no subject)'}</h3>
                          <div className="grid sm:grid-cols-2 gap-x-4 mt-2 text-xs text-white/70">
                            <p><span className="text-white/50">From:</span> {emailLogPreview.from || '—'}</p>
                            <p><span className="text-white/50">To:</span> {emailLogPreview.to || '—'}</p>
                            <p><span className="text-white/50">When:</span> {emailLogPreview.timestamp ? formatCT(emailLogPreview.timestamp) : '—'}</p>
                            <p>
                              <span className="text-white/50">Status:</span>{' '}
                              {emailLogPreview.success
                                ? <span className="text-emerald-300 font-semibold">SENT</span>
                                : <span className="text-red-300 font-semibold">FAILED</span>}
                            </p>
                          </div>
                          {!emailLogPreview.success && emailLogPreview.error && (
                            <p className="mt-2 text-xs text-red-200 font-mono break-all">{emailLogPreview.error}</p>
                          )}
                        </div>
                        <button
                          onClick={() => setEmailLogPreview(null)}
                          className="text-white/60 hover:text-white transition-colors p-1 -mt-1 -mr-1"
                          aria-label="Close"
                          data-testid="email-log-preview-close"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="flex items-center gap-1 px-6 pt-3 border-b border-gray-100 bg-gray-50">
                        {emailLogPreview.body_html && (
                          <button
                            onClick={() => setEmailLogPreviewMode('html')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors ${
                              emailLogPreviewMode === 'html'
                                ? 'bg-white text-navy-900 border border-gray-200 border-b-white -mb-px'
                                : 'text-gray-500 hover:text-navy-900'
                            }`}
                          >
                            Formatted
                          </button>
                        )}
                        <button
                          onClick={() => setEmailLogPreviewMode('text')}
                          className={`px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors ${
                            emailLogPreviewMode === 'text'
                              ? 'bg-white text-navy-900 border border-gray-200 border-b-white -mb-px'
                              : 'text-gray-500 hover:text-navy-900'
                          }`}
                        >
                          Plain text
                        </button>
                      </div>

                      <div className="flex-1 overflow-auto bg-white">
                        {emailLogPreviewMode === 'html' && emailLogPreview.body_html ? (
                          <iframe
                            title="Email preview"
                            srcDoc={emailLogPreview.body_html}
                            sandbox=""
                            className="w-full h-[60vh] border-0"
                            data-testid="email-log-preview-html"
                          />
                        ) : emailLogPreview.body_text ? (
                          <pre className="px-6 py-4 text-sm text-navy-900 whitespace-pre-wrap break-words font-mono leading-relaxed" data-testid="email-log-preview-text">
                            {emailLogPreview.body_text}
                          </pre>
                        ) : (
                          <p className="px-6 py-10 text-center text-sm text-gray-400">
                            No body captured for this entry. (Sent before the click-to-view feature was added.)
                          </p>
                        )}
                      </div>

                      {/* Footer actions — Resend button */}
                      {emailLogPreview.id && (emailLogPreview.body_text || emailLogPreview.body_html) && (
                        <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-gray-200 bg-gray-50">
                          <p className="text-xs text-gray-500">
                            {emailLogPreview.success
                              ? 'Send this email again to the same recipient.'
                              : 'This email failed. Retry sending with the current SMTP settings.'}
                          </p>
                          <button
                            type="button"
                            onClick={resendEmailLogEntry}
                            disabled={emailLogResending}
                            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                              emailLogPreview.success
                                ? 'bg-navy-900 hover:bg-navy-800 text-white'
                                : 'bg-red-600 hover:bg-red-700 text-white'
                            } disabled:opacity-60 disabled:cursor-not-allowed`}
                            data-testid="email-log-resend-btn"
                          >
                            {emailLogResending ? 'Resending…' : (emailLogPreview.success ? 'Resend' : 'Retry send')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Backup & Restore Tab */}
          {activeTab === 'backup' && isSuperAdmin() && (
            <div className="space-y-6">
              {/* Export Backup */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Download className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-navy-900 mb-1">Export Backup</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Download a complete backup of all your site content, settings, and configurations as a JSON file.
                    </p>
                    
                    {backupInfo && (
                      <div className="bg-white rounded-lg p-3 mb-4 border">
                        <p className="text-xs font-medium text-gray-500 mb-2">Data to be exported:</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {Object.entries(backupInfo.collections || {}).map(([name, data]) => (
                            <div key={name} className="flex justify-between">
                              <span className="text-gray-600 capitalize">{name.replace(/_/g, ' ')}</span>
                              <span className="text-navy-900 font-medium">{data.document_count} items</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <Button
                      onClick={handleExportBackup}
                      disabled={isExporting}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {isExporting ? 'Exporting...' : 'Download Backup'}
                      <Download className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Import/Restore Backup */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <Upload className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-navy-900 mb-1">Restore from Backup</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Upload a previously exported backup file to restore your site content and settings.
                    </p>
                    
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Warning</p>
                          <p className="text-xs text-amber-700">
                            Restoring a backup will <strong>replace all current content</strong> with the backup data. This action cannot be undone.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json"
                      onChange={handleImportBackup}
                      className="hidden"
                    />
                    
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isImporting}
                      variant="outline"
                      className="border-amber-500 text-amber-700 hover:bg-amber-50"
                    >
                      {isImporting ? 'Restoring...' : 'Select Backup File'}
                      <Upload className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Email Backup Schedule */}
              <BackupScheduleCard token={token} API={API} />

              {/* Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Backup Tips</p>
                    <ul className="text-xs text-blue-700 mt-1 space-y-1">
                      <li>• Create regular backups before making major changes</li>
                      <li>• Store backup files in a secure location</li>
                      <li>• Backups include all content, settings, and panel layouts</li>
                      <li>• Image URLs are included but not the actual image files</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && isSuperAdmin() && (
            <div className="space-y-6" data-testid="users-panel-container">
              <AdminUsers token={token} currentUserEmail={user?.email} />
            </div>
          )}

          {/* Leads Tab */}
          {activeTab === 'leads' && isSuperAdmin() && (
            <div className="space-y-6" data-testid="leads-panel-container">
              <AdminLeads token={token} />
            </div>
          )}
        </div>
      </div>
    </div>
    
    {/* Content Manager */}
    <ContentManager
      isOpen={showContentManager}
      onClose={() => setShowContentManager(false)}
      token={token}
      onContentChange={onContentChange}
    />

    {/* Zoomed Level-2 snapshot lightbox */}
    {zoomedSnapshot && (
      <div
        className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
        onClick={() => setZoomedSnapshot(null)}
        data-testid="snapshot-lightbox"
      >
        <div
          className="relative max-w-3xl w-full bg-slate-950 rounded-xl overflow-hidden border border-cyan-400/30"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setZoomedSnapshot(null)}
            className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 text-white hover:bg-black flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
          {zoomedSnapshot.photo && (
            <img
              src={zoomedSnapshot.photo}
              alt={`${zoomedSnapshot.email} snapshot`}
              className="w-full max-h-[75vh] object-contain bg-black"
            />
          )}
          <div className="px-5 py-4 text-sm">
            <div className="font-semibold text-white">{zoomedSnapshot.email}</div>
            <div className="text-xs text-slate-400 font-mono mt-1">
              {zoomedSnapshot.timestamp
                ? formatCT(zoomedSnapshot.timestamp)
                : ''}
              {' · '}IP {zoomedSnapshot.ip}
            </div>
            {zoomedSnapshot.user_agent && (
              <div className="text-[10px] text-slate-500 font-mono mt-1 break-all">
                {zoomedSnapshot.user_agent}
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Clone Panel Dialog */}
    <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Panel</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-gray-600">
            Create a new "{clonablePanelTypes[cloneSource?.type || cloneSource?.id]?.name || 'custom'}" panel. 
            This will create a new section with its own content that you can edit separately.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-2">
              Panel Name *
            </label>
            <Input
              value={newPanelName}
              onChange={(e) => setNewPanelName(e.target.value)}
              placeholder={`e.g., Partners, Clients, ${clonablePanelTypes[cloneSource?.type || cloneSource?.id]?.name || 'New Section'}`}
              onKeyDown={(e) => e.key === 'Enter' && handleClonePanel()}
            />
            <p className="text-xs text-gray-500 mt-1">
              This name will appear in the navigation and admin panel
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowCloneDialog(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleClonePanel}
              className="flex-1 bg-gold-500 hover:bg-gold-600 text-navy-900"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Panel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default AdminPanel;
